import { AttributionEffectEventType as AttributionOutcomeType } from "@redotech/crdb-enums/attribution/attribution-effect-event-type";
import { AttributionModel as AttributionPreset } from "@redotech/crdb-enums/attribution/attribution-model";
import { durationToSeconds, TimeUnit } from "@redotech/redo-common-model/time";
import * as z from "zod";

export { AttributionPreset };

/** Subset of TimeUnit values the attribution UI exposes. */
export const ATTRIBUTION_TIME_UNITS = [
  TimeUnit.MINUTE,
  TimeUnit.HOUR,
  TimeUnit.DAY,
] as const;

/**
 * The longest attribution window, everywhere. Persistence rejects anything
 * larger and the analytics filters stop here. Raise it when merchants ask.
 */
export const MAX_ATTRIBUTION_WINDOW_DAYS = 30;

/** Time window looked backward from an outcome. */
export const durationSchema = z
  .object({
    value: z.number().int().positive(),
    unit: z.enum(ATTRIBUTION_TIME_UNITS),
  })
  .refine(
    (d) =>
      durationToSeconds(d.value, d.unit) <=
      MAX_ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60,
    { message: `window exceeds ${MAX_ATTRIBUTION_WINDOW_DAYS} days` },
  );
export type Duration = z.infer<typeof durationSchema>;

/** A way a touchpoint can qualify for an order's credit. */
export enum AttributionModel {
  /** The evidence occurred within its window before the order. */
  INFERRED = "inferred",
  /** A provable per-touchpoint signal links it to the order. */
  DIRECT = "direct",
}

/** Which qualifiers to union for a run; at least one must be true. */
export type QualifierSelection = Record<AttributionModel, boolean>;

/** How a qualifying order's credit is split among its touchpoints. */
export enum AttributionDistribution {
  /** Most recent qualifying touchpoint gets full credit. */
  LAST_TOUCH = "last_touch",
  /** Qualifying touchpoints split credit evenly. */
  MULTI_TOUCH_LINEAR = "linear",
}

/**
 * Merchant-facing preset persisted as the default report view. Resolves to the
 * query axes via {@link ATTRIBUTION_MODEL_RESOLUTION}.
 */
export interface AttributionAxes {
  qualifiers: QualifierSelection;
  distribution: AttributionDistribution;
}

export const ATTRIBUTION_MODEL_RESOLUTION: Record<
  AttributionPreset,
  AttributionAxes
> = {
  [AttributionPreset.LastTouch]: {
    qualifiers: {
      [AttributionModel.INFERRED]: true,
      [AttributionModel.DIRECT]: false,
    },
    distribution: AttributionDistribution.LAST_TOUCH,
  },
  [AttributionPreset.Linear]: {
    qualifiers: {
      [AttributionModel.INFERRED]: true,
      [AttributionModel.DIRECT]: false,
    },
    distribution: AttributionDistribution.MULTI_TOUCH_LINEAR,
  },
  [AttributionPreset.Direct]: {
    qualifiers: {
      [AttributionModel.INFERRED]: false,
      [AttributionModel.DIRECT]: true,
    },
    distribution: AttributionDistribution.LAST_TOUCH,
  },
};

/**
 * The channel a touchpoint went out on: the UI's grouping of evidence types and
 * the keys for direct attribution config. Recover and Commerce Agent messages
 * are emails and texts like any other.
 */
export enum AttributionTouchpoint {
  EMAIL = "email",
  SMS = "sms",
}

/**
 * Something suggesting a touchpoint caused an outcome.
 * TODO: CRDB still names this `attribution_cause_event_type`, and outcomes
 * `attribution_effect_event_type` (imported here as AttributionOutcomeType);
 * rename both there too.
 */
export enum AttributionEvidenceType {
  // Email
  EMAIL_SEND = "email_send",
  EMAIL_DELIVERED = "email_delivered",
  EMAIL_OPEN = "email_open",
  EMAIL_CLICK = "email_click",
  // SMS — SMS_DELIVERED and SMS_OPEN intentionally omitted: carriers don't
  // reliably report either, so they'd be misleading qualifying signals.
  SMS_SEND = "sms_send",
  SMS_CLICK = "sms_click",
}

/** Per-evidence-type qualifying config. Disabled entries don't contribute. */
export const evidenceConfigSchema = z.object({
  enabled: z.boolean(),
  window: durationSchema,
});
export type EvidenceConfig = z.infer<typeof evidenceConfigSchema>;

/**
 * Per-touchpoint config for the DIRECT model.
 */
export const directConfigSchema = z.object({
  enabled: z.boolean(),
  window: durationSchema,
});
export type DirectConfig = z.infer<typeof directConfigSchema>;

/**
 * One optional config per enum value: unknown keys drop and missing keys stay unset, so a
 * deploy that widens the enum can't fail old pods or tabs. The repo fills unset keys on read.
 */
function configPerEnumValue<E extends string, V extends z.ZodType>(
  values: readonly E[],
  valueSchema: V,
) {
  return z.object(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    Object.fromEntries(
      values.map((value) => [value, valueSchema.optional()]),
    ) as { [K in E]: z.ZodOptional<V> },
  );
}

/** Attribution config for one outcome. Every outcome shares this shape. */
export const outcomeAttributionConfigSchema = z.object({
  /** Which model loads first in reports. Other models may still be computed on demand for the same data. */
  defaultModel: z.enum(AttributionPreset),
  /**
   * Inferred attribution (last-touch / multi-touch linear): credit is inferred from a
   * piece of evidence landing within a window.
   */
  inferred: configPerEnumValue(
    Object.values(AttributionEvidenceType),
    evidenceConfigSchema,
  ),
  /**
   * Direct attribution: a provable per-touchpoint signal linking the outcome to
   * the touchpoint. Less granular than inferred (touchpoint-level).
   */
  direct: configPerEnumValue(
    Object.values(AttributionTouchpoint),
    directConfigSchema,
  ),
});
export type OutcomeAttributionConfig = z.infer<
  typeof outcomeAttributionConfigSchema
>;

/** Per-team attribution settings: the same config shape for every outcome type. */
export const attributionSettingsSchema = z.record(
  z.enum(AttributionOutcomeType),
  outcomeAttributionConfigSchema,
);
export type AttributionSettings = z.infer<typeof attributionSettingsSchema>;
